import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';
import { convertMoneyToMinorUnits, formatMoney } from '@theme/money-formatting';

/**
 * Two-thumb price slider for the collection and search price filter.
 *
 * An additive layer over Horizon's <price-facet-component> (assets/facets.js):
 * a moved thumb writes a whole-currency value into Horizon's matching min/max
 * input, then calls Horizon's public updatePriceFilterAndResults(). URL building,
 * validation and section re-rendering all stay upstream. A thumb parked at its
 * end stop clears its input, which drops that bound from the filter URL.
 *
 * @typedef {Object} PriceRangeRefs
 * @property {HTMLInputElement} rangeMin
 * @property {HTMLInputElement} rangeMax
 * @property {HTMLElement} [rangeLabelMin]
 * @property {HTMLElement} [rangeLabelMax]
 *
 * @extends {Component<PriceRangeRefs>}
 */
class RootsPriceRangeComponent extends Component {
  requiredRefs = ['rangeMin', 'rangeMax'];

  // Liquid renders the full visual state (thumbs, fill, labels), so a filter
  // re-render morphs it back correctly. This class only reacts to the user and
  // reads limits live from the DOM rather than caching them.

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#onKeyDown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#onKeyDown);
  }

  /** Highest price in the current results, in whole currency units. */
  get #rangeMax() {
    return Number(this.dataset.rangeMax) || 0;
  }

  /** Slider top end (range max rounded up to a whole step). */
  get #sliderMax() {
    return Number(this.refs.rangeMax.max) || 0;
  }

  /**
   * Live update while dragging: keep thumbs from crossing, redraw, and mirror
   * the moved thumb into Horizon's input (no request yet).
   * @param {Event} event
   */
  onSlide(event) {
    const { rangeMin, rangeMax } = this.refs;
    const movedMin = event.target === rangeMin;

    if (Number(rangeMin.value) > Number(rangeMax.value)) {
      if (movedMin) {
        rangeMin.value = rangeMax.value;
      } else {
        rangeMax.value = rangeMin.value;
      }
    }

    this.#raise(movedMin ? rangeMin : rangeMax);
    this.#render();
    this.#writeToFacet(movedMin);
  }

  /**
   * Thumb released (or arrow key pressed). Debounced so holding an arrow key
   * sends one filter request, not one per step.
   */
  onCommit = debounce(() => {
    const facet = this.#priceFacet;
    if (!facet) return;

    // Horizon clamps each input against the other's last *committed* value.
    // Refresh those limits so moving both thumbs before a commit isn't undone.
    const { minInput, maxInput } = facet.refs;
    minInput.setAttribute('data-max', maxInput.value || maxInput.getAttribute('data-max') || '');
    maxInput.setAttribute('data-min', minInput.value || '0');

    facet.updatePriceFilterAndResults();
  }, 300);

  /**
   * @param {boolean} isMin - Whether the min thumb moved
   */
  #writeToFacet(isMin) {
    const facet = this.#priceFacet;
    if (!facet) return;

    const { minInput, maxInput } = facet.refs;

    if (isMin) {
      const value = Number(this.refs.rangeMin.value);
      minInput.value = value <= 0 ? '' : String(value);
    } else {
      const value = Number(this.refs.rangeMax.value);
      maxInput.value = value >= this.#rangeMax ? '' : String(value);
    }
  }

  #render() {
    const { rangeMin, rangeMax, rangeLabelMin, rangeLabelMax } = this.refs;
    const min = Number(rangeMin.value);
    const max = Math.min(Number(rangeMax.value), this.#rangeMax);

    const sliderMax = this.#sliderMax;
    /** @param {number} value */
    const percent = (value) => `${sliderMax ? (value / sliderMax) * 100 : 0}%`;
    this.style.setProperty('--roots-range-start', percent(Number(rangeMin.value)));
    this.style.setProperty('--roots-range-end', percent(Number(rangeMax.value)));

    const minText = this.#format(min);
    const maxText = this.#format(max);
    rangeMin.setAttribute('aria-valuetext', minText);
    rangeMax.setAttribute('aria-valuetext', maxText);
    if (rangeLabelMin) rangeLabelMin.textContent = minText;
    if (rangeLabelMax) rangeLabelMax.textContent = maxText;
  }

  /**
   * Formats whole currency units with the shop's money format, without decimals.
   * @param {number} amount
   * @returns {string}
   */
  #format(amount) {
    const currency = this.dataset.currency ?? 'USD';
    const unit = convertMoneyToMinorUnits('1', currency) ?? 100;
    const format = (this.dataset.moneyFormat || '{{amount}}')
      .replace(/{{\s*amount_with_comma_separator\s*}}/, '{{amount_no_decimals_with_comma_separator}}')
      .replace(/{{\s*amount\s*}}/, '{{amount_no_decimals}}');

    return formatMoney(amount * unit, format, currency);
  }

  /**
   * @param {HTMLInputElement} input - The thumb that should sit above the other
   */
  #raise(input) {
    const { rangeMin, rangeMax } = this.refs;
    rangeMin.toggleAttribute('data-on-top', input === rangeMin);
    rangeMax.toggleAttribute('data-on-top', input === rangeMax);
  }

  /** @returns {any} Horizon's PriceFacetComponent (typed loosely: it isn't exported) */
  get #priceFacet() {
    return this.closest('price-facet-component');
  }

  /**
   * Horizon's price facet swallows keys other than digits and arrows (meant for
   * its text inputs). Keep Home/End/PageUp/PageDown working on the slider.
   * @param {KeyboardEvent} event
   */
  #onKeyDown = (event) => {
    if (['Home', 'End', 'PageUp', 'PageDown'].includes(event.key)) event.stopPropagation();
  };
}

if (!customElements.get('roots-price-range-component')) {
  customElements.define('roots-price-range-component', RootsPriceRangeComponent);
}
